import { Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { TokenProvider } from "@/components/providers/token-provider";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { ScrollToTop } from "@/components/ScrollToTop";
// Pages
import HomePage from "@/pages/Home";
import LoginPage from "@/pages/Login";
import SSOCallbackPage from "@/pages/SSOCallback";
import ProfilePage from "@/pages/Profile";
import OrganizationPage from "@/pages/Organization";
import ApplicationPage from "@/pages/applications/Application";
import NotificationsPage from "@/pages/settings/NotificationsPage";

// Layout wrapper for pages with Header/Footer
function MainLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Header />
      <main className="min-h-[calc(100vh-4rem)]">{children}</main>
    </>
  );
}

// Layout wrapper for full-screen pages (like applications)
function FullScreenLayout({ children }: { children: React.ReactNode }) {
  return <main className="h-screen">{children}</main>;
}

function AppContent() {
  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      {/* SSO redirect landing — exchanges the KC code for session cookies
          and forwards to the originally-requested page (or /). */}
      <Route path="/sso/callback" element={<SSOCallbackPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <HomePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Profile page */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfilePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Organization page */}
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OrganizationPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Alias for organization-profile (used by sidebar) */}
      <Route
        path="/organization-profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OrganizationPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Notification Settings page */}
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Application pages (full screen, protected) */}
      <Route
        path="/applications/:appSlug/*"
        element={
          <ProtectedRoute>
            <FullScreenLayout>
              <ApplicationPage />
            </FullScreenLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect to home */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <HomePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <TokenProvider>
        <AppContent />
        <Toaster />
      </TokenProvider>
    </ThemeProvider>
  );
}

export default App;
